export const CONSTANTS = {
  SITE_NAME: "Major League Prompting",
  TEXT_STYLES: {
    heading: 'font-bold',
    paragraph: 'text-gray-700',
    code: 'bg-gray-100 p-1 rounded'
  },
  NAVIGATION: [
    {
      name: "Features",
      url: "#about"
    },
    {
      name: "Competitions",
      url: "#competitions"
    },
    {
      name: "How It Works",
      url: "#how-it-works"
    },
    {
      name: "Testimonials",
      url: "#testimonials"
    }
  ],
  HERO: {
    TAG: "In Beta",
    TITLE: "Where prompt engineers compete for",
    HIGHLIGHT: "greatness.",
    SUBTITLE: "Join our exclusive platform for timed challenges, showcase your skills, and compete against the best prompt engineers in the industry."
  },
  FEATURES: {
    TAG: "Why Join Us",
    TITLE: "Your Arena for Prompt Engineering",
    SUBTITLE: "We've built the perfect platform for AI prompt engineers to showcase skills, compete, and earn recognition.",
    ITEMS: [
      {
        icon: "Trophy",
        title: "Competitive Challenges",
        description: "Participate in timed contests designed to push your creative problem-solving abilities to the limit."
      },
      {
        icon: "Code",
        title: "Skill Showcase",
        description: "Demonstrate your prompt engineering talents on a professional platform with real-world applications."
      },
      {
        icon: "Clock",
        title: "Exclusive Competitions",
        description: "Access invite-only events with opportunities to earn recognition and prizes in the industry."
      }
    ]
  },
  COMPETITION_TYPES: {
    TAG: "Competition Types",
    TITLE: "Different Ways to Compete",
    SUBTITLE: "We offer various competition formats to test different aspects of prompt engineering skills.",
    TABS: [
      {
        id: "competitive",
        name: "Competitive"
      },
      {
        id: "creative",
        name: "Creative"
      },
      {
        id: "technical",
        name: "Technical"
      }
    ],
    CONTENT: [
      {
        id: "competitive",
        title: "Competitive Challenges",
        description: "Race against the clock and other engineers to create the most effective prompts that satisfy specific requirements.",
        features: [
          "Timed challenges with specific scoring metrics",
          "Leaderboards with ranking and performance analysis",
          "Weekly tournaments with increasing difficulty levels",
          "Head-to-head knockout competitions"
        ],
        example: {
          type: "Speed Challenge",
          difficulty: "Advanced",
          challenge: "Create a prompt that generates a detailed marketing campaign for a sustainable fashion brand, including taglines, target audience analysis, and channel strategy.",
          solution: "You are an expert marketing strategist specializing in sustainable fashion brands. Create a comprehensive marketing campaign for [BRAND] that sells eco-friendly clothing made from recycled materials.\n\nInclude the following:\n1. Three compelling taglines that emphasize sustainability\n2. Detailed target audience analysis (demographics, psychographics, behavior)\n3. Channel strategy with specific recommendations for social media, email, and partnerships\n4. Content pillars that align with sustainable values\n5. KPIs to measure campaign success"
        }
      },
      {
        id: "creative",
        title: "Creative Challenges",
        description: "Push the boundaries of what's possible with AI by crafting prompts that generate unique, imaginative content.",
        features: [
          "Open-ended challenges with subjective judging",
          "Community voting and featured showcases",
          "Collaboration opportunities with artists and writers",
          "Themed contests aligned with current events"
        ],
        example: {
          type: "Creative Challenge",
          difficulty: "Intermediate",
          challenge: "Design a prompt that generates an original short story in the style of magical realism, featuring a character who discovers they can communicate with household objects.",
          solution: "Write a 500-word magical realism short story about a person who wakes up one morning to discover they can hear the thoughts and feelings of everyday household objects. The story should blend the mundane with the fantastical in the tradition of García Márquez and Borges.\n\nFollow these parameters:\n- Begin with an ordinary morning routine disrupted by the first 'conversation'\n- Include at least three different household objects with distinct personalities\n- The protagonist should be skeptical at first, then gradually accept this new reality\n- End with a revelation that changes how the protagonist views their life\n- Use sensory details and matter-of-fact narration when describing magical elements"
        }
      },
      {
        id: "technical",
        title: "Technical Challenges",
        description: "Focus on creating precise, efficient prompts that solve complex technical problems across various domains.",
        features: [
          "Industry-specific challenges for specialized domains",
          "Optimization tasks for response quality and efficiency",
          "Documentation and reproducibility requirements",
          "Real-world application scenarios from partner companies"
        ],
        example: {
          type: "Technical Challenge",
          difficulty: "Expert",
          challenge: "Develop a prompt that can explain complex machine learning concepts to high school students, with appropriate analogies and visualizations that a model could describe.",
          solution: "You are an exceptional teacher with expertise in machine learning and a talent for making complex topics accessible to beginners. Your task is to explain [SPECIFIC_ML_CONCEPT] to high school students who have basic math knowledge but no ML background.\n\nYour explanation should include:\n1. A simple, real-world analogy that captures the essence of the concept\n2. A step-by-step breakdown of how the concept works, avoiding jargon\n3. Descriptions of 2-3 visualizations that would help students understand (describe what the visualizations show, not how to create them)\n4. Practical examples of where this concept is used in technology they interact with daily\n5. A brief interactive thought experiment that lets students mentally apply the concept\n\nMaintain an encouraging, curious tone throughout. Use simple language but don't oversimplify the actual mechanics to the point of being incorrect."
        }
      }
    ]
  },
  HOW_IT_WORKS: {
    TAG: "Getting Started",
    TITLE: "How It Works",
    SUBTITLE: "Join our community of prompt engineers in just a few simple steps.",
    STEPS: [
      {
        title: "Sign Up for Access",
        description: "Create an account to join our platform. New members get exclusive benefits and access to competitions.",
        action: {
          label: "Join today",
          url: "#signup"
        }
      },
      {
        title: "Create Your Engineer Profile",
        description: "Set up your professional profile showcasing your skills, experience, and areas of expertise. Connect with other prompt engineers and build your reputation.",
        action: null
      },
      {
        title: "Join Scheduled Competitions",
        description: "Browse upcoming challenges that match your skills and interests. Register for events that fit your schedule and prepare for the competition.",
        action: null
      },
      {
        title: "Compete and Rise Up the Ranks",
        description: "Participate in timed challenges, submit your solutions, and get evaluated based on effectiveness, creativity, and efficiency. Earn points and climb the leaderboard.",
        action: null
      }
    ]
  },
  TESTIMONIALS: {
    TAG: "Community Voices",
    TITLE: "What Engineers Are Saying",
    SUBTITLE: "Hear from our members who have participated in our competitions.",
    ITEMS: [
      {
        quote: "These competitions pushed me to develop prompt engineering techniques I never would have discovered otherwise. The timed challenges really simulate real-world pressure situations.",
        name: "Alex Chen",
        title: "Senior AI Prompt Engineer",
        avatar: null
      },
      {
        quote: "The caliber of talent here is incredible. Every contest teaches me something new about the art of prompt engineering. It's like a masterclass with each competition.",
        name: "Jordan Taylor",
        title: "Creative AI Specialist",
        avatar: null
      },
      {
        quote: "I've improved more in two months of competing than in a year of working solo. The feedback system and community discussions are invaluable for growth.",
        name: "Maya Patel",
        title: "NLP Researcher",
        avatar: null
      },
      {
        quote: "As someone transitioning into AI from another field, the structured challenges have given me a clear path to building my skills and portfolio in prompt engineering.",
        name: "Theo Williams",
        title: "AI Career Switcher",
        avatar: null
      }
    ]
  },
  SIGNUP: {
    TAG: "Open Registration",
    TITLE: "Join Today",
    SUBTITLE: "Join our platform and start competing. Members receive exclusive benefits and access to special competitions."
  },
  REGISTRATION_MODAL: {
    TITLE: "Create Your Account",
    SUBTITLE: "Join the Prompt Engineering Community",
    DESCRIPTION: "Step 1 of 3: Let's start with your email",
    EMAIL_LABEL: "Email Address",
    EMAIL_PLACEHOLDER: "yourname@example.com",
    EMAIL_HELP_TEXT: "We'll use this email to create your account and send competition updates.",
    BENEFITS_TITLE: "As a registered member, you'll get:",
    BENEFITS: [
      "Access to exclusive challenges and competitions",
      "Performance tracking and skill development metrics",
      "Connect with top industry prompt engineers",
      "Opportunity to win prizes and recognition"
    ],
    BUTTON_TEXT: "Create Account & Continue",
    PRIVACY_TEXT: "By creating an account, you agree to our Terms of Service and Privacy Policy."
  },
  FOOTER: {
    SOCIAL: [
      {
        icon: "Twitter",
        url: "https://twitter.com/videogame_ai"
      },
      {
        icon: "GitHub",
        url: "https://github.com/volter-ai"
      },
      {
        icon: "Discord",
        url: "https://discord.gg/YWnDCT5EGz"
      }
    ],
    COLUMNS: [
      {
        title: "Platform",
        links: [
          {
            name: "Competitions",
            url: "https://playdot.ai"
          },
          {
            name: "Leaderboard",
            url: "https://playdot.ai"
          },
          {
            name: "Resources",
            url: "https://playdot.ai"
          },
          {
            name: "Community",
            url: "https://discord.gg/YWnDCT5EGz"
          }
        ]
      },
      {
        title: "Company",
        links: [
          {
            name: "About Us",
            url: "#about"
          },
          {
            name: "Careers",
            url: "https://playdot.ai"
          },
          {
            name: "Blog",
            url: "https://playdot.ai"
          },
          {
            name: "Contact",
            url: "mailto:chris@volter.ai"
          }
        ]
      },
      {
        title: "Support",
        links: [
          {
            name: "Help Center",
            url: "https://discord.gg/YWnDCT5EGz"
          },
          {
            name: "FAQs",
            url: "https://discord.gg/YWnDCT5EGz"
          },
          {
            name: "Rules",
            url: "https://discord.gg/YWnDCT5EGz"
          },
          {
            name: "Feedback",
            url: "https://discord.gg/YWnDCT5EGz"
          }
        ]
      }
    ],
    LEGAL: [
      {
        name: "Privacy Policy",
        url: "https://discord.gg/YWnDCT5EGz"
      },
      {
        name: "Terms of Service",
        url: "https://discord.gg/YWnDCT5EGz"
      },
      {
        name: "Code of Conduct",
        url: "https://discord.gg/YWnDCT5EGz"
      }
    ]
  },
  UTM_PARAMS: {
    SOURCE: "website",
    MEDIUM: "landing_page",
    CAMPAIGN: "signup_flow",
    TERM: "",
    CONTENT: ""
  },
  REFERRAL: {
    TAG: "Referral Program",
    TITLE: "Invite Friends & Grow Together",
    SUBTITLE: "Share your unique referral link with friends and help build our community of prompt engineers.",
    FORM: {
      NAME_LABEL: "Your Name",
      NAME_PLACEHOLDER: "Enter your name",
      NAME_HELP_TEXT: "This will be included in your referral link.",
      BUTTON_TEXT: "Generate Referral Link"
    },
    LINK_SECTION: {
      TITLE: "Your Referral Link",
      COPY_BUTTON: "Copy Link",
      COPIED_MESSAGE: "Copied!",
      SOCIAL_SHARE_TITLE: "Share via:"
    },
    BENEFITS: {
      TITLE: "Benefits of Referring",
      ITEMS: [
        "Earn exclusive badges on your profile",
        "Get early access to special competitions",
        "Build a stronger community of skilled engineers",
        "Help friends discover their prompt engineering potential"
      ]
    },
    HOW_IT_WORKS: {
      TITLE: "How Referrals Work",
      STEPS: [
        "Enter your name to generate a custom referral link",
        "Share your link with friends interested in prompt engineering",
        "When they sign up using your link, you both get credited",
        "Track your referrals in your profile dashboard"
      ]
    },
    UTM_PARAMS: {
      SOURCE: "referral",
      MEDIUM: "direct",
      CAMPAIGN: "friend_referral",
      CONTENT: "" // Will be filled with user name
    }
  }
}
